import { useCallback, useReducer } from 'react';
import { ChartSettingsT, VizType } from 'business/definitions';
import { DEFAULT_PAGE_SIZE } from 'business/constants';
import {
    getAvailableViz,
    selectBestAvailableViz,
} from 'helpers/chart/availableVizHelpers';
import { getResetSettings, shouldResetIsStacked } from './helpers';

export type SettingsState = {
    chartTitle: string;
    chartSettings: ChartSettingsT;
    availableViz: VizType[];
    selectedViz: VizType;
    reloadChart: boolean;
    columns?: ResponseColumnT[];
    tags: string[];
};

export const initialSettingsState: SettingsState = {
    chartTitle: '',
    chartSettings: {
        showLegend: true,
        isStackedBars: false,
        isYearlyComparison: false,
        metricsCumulated: [],
        useSmartMultiMetrics: true,
        palette: 'manty',
        rowHeight: 'md',
        pageSize: DEFAULT_PAGE_SIZE,
        showAggregates: true,
        showFilters: true,
        reverseGroupBy: false,
        decimalPrecision: 2,
        displayBy: 'default',
        verticalAxisWidth: 'sm',
    },
    tags: [],
    availableViz: [],
    selectedViz: 'table',
    reloadChart: false,
    columns: undefined,
};

export enum SettingsActionType {
    resetSettings,
    applyChartSettings,
    updateVizSettings,
    onQuerySucceeded,
    setReloadChart,
}

export type VizSettingsPayload = {
    title?: string;
    vizType?: VizType;
    settings?: ChartSettingsT;
    tags?: string[];
};

export type OnQuerySucceededPayload = {
    data: ResponseDataT[];
    columns: ResponseColumnT[];
    queryMetrics: QueryMetricT[];
    queryDimensions: QueryDimensionT[];
};

type SettingsAction =
    | { type: SettingsActionType.resetSettings }
    | { type: SettingsActionType.applyChartSettings; payload: ChartSettingsT }
    | {
          type: SettingsActionType.updateVizSettings;
          payload: VizSettingsPayload;
      }
    | {
          type: SettingsActionType.onQuerySucceeded;
          payload: OnQuerySucceededPayload;
      }
    | { type: SettingsActionType.setReloadChart; payload: { value: boolean } };

export const settingsReducer = (
    state: SettingsState,
    action: SettingsAction,
): SettingsState => {
    switch (action.type) {
        case SettingsActionType.resetSettings:
            return { ...initialSettingsState };
        case SettingsActionType.applyChartSettings:
            return {
                ...state,
                chartSettings: {
                    ...state.chartSettings,
                    ...action.payload,
                },
            };
        case SettingsActionType.updateVizSettings: {
            const { payload } = action;
            return {
                ...state,
                chartTitle:
                    payload.title == null ? state.chartTitle : payload.title,
                selectedViz: payload.vizType
                    ? payload.vizType
                    : state.selectedViz,
                chartSettings: {
                    ...state.chartSettings,
                    ...payload.settings,
                },
                tags: payload.tags == null ? state.tags : payload.tags,
            };
        }
        case SettingsActionType.onQuerySucceeded: {
            const { payload } = action;
            const availableViz = getAvailableViz(
                payload.data,
                payload.columns,
                payload.queryMetrics,
                payload.queryDimensions,
            );
            const selectedViz = selectBestAvailableViz(
                state.selectedViz,
                availableViz,
            );
            const resetSettings = getResetSettings(
                payload.queryMetrics,
                payload.columns,
            );

            return {
                ...state,
                availableViz,
                selectedViz,
                columns: payload.columns,
                chartSettings: {
                    ...state.chartSettings,
                    ...resetSettings,
                },
            };
        }
        case SettingsActionType.setReloadChart:
            return { ...state, reloadChart: action.payload.value };
        default:
            return state;
    }
};

export const useSettingsReducer = () => {
    const [settings, dispatch] = useReducer(
        settingsReducer,
        initialSettingsState,
    );

    const resetSettings = useCallback(
        () => dispatch({ type: SettingsActionType.resetSettings }),
        [],
    );

    const applyChartSettings = useCallback((chartSettings: ChartSettingsT) => {
        if (shouldResetIsStacked(chartSettings)) {
            chartSettings.isStackedBars = false;
        }
        dispatch({
            type: SettingsActionType.applyChartSettings,
            payload: chartSettings,
        });
    }, []);

    const updateVizSettings = useCallback(
        (vizSettings: VizSettingsPayload) =>
            dispatch({
                type: SettingsActionType.updateVizSettings,
                payload: vizSettings,
            }),
        [],
    );

    const onQuerySucceeded = useCallback(
        (payload: OnQuerySucceededPayload) =>
            dispatch({
                type: SettingsActionType.onQuerySucceeded,
                payload,
            }),
        [],
    );

    const setReloadChart = useCallback(
        (value: boolean) =>
            dispatch({
                type: SettingsActionType.setReloadChart,
                payload: { value },
            }),
        [],
    );

    return {
        settings,
        resetSettings,
        applyChartSettings,
        updateVizSettings,
        onQuerySucceeded,
        setReloadChart,
    };
};
